import Box from '@mui/material/Box';
import { useTheme } from '@mui/material';
import { useState, useEffect, useRef, type RefObject } from 'react';
import WebNavSidebar, { ToolOption } from './WebNavSidebar';
import ToolPanel from './ToolPanel';
import type { ColumnEditorInfo } from './ColumnEditorPanel';
import {
  useCampaignContextController,
  useSpreadsheetViewType
} from '../../context/CampaignContext';
import { CampaignProspectType } from '../../api/campaigns';
import type { SpreadsheetRefHandle } from '../spreadsheet/spreadsheetTypes';
import { resolveCampaignHeaderBackground } from '../../theme';

interface SidebarComponentProps {
  isAdmin: boolean;
  isManager: boolean;
  hasAccessToAutomation: boolean;
  isBeta: boolean;
  columnEditorColumn: ColumnEditorInfo | null;
  /** When true, enrichment or prioritization locks the spreadsheet; column editor, prioritization, and target persona write operations are disabled. */
  spreadsheetJobLocked?: boolean;
  /** When true, enrichment (not prioritization) is locking the spreadsheet — enrichment-specific help text only. */
  spreadsheetLockedDuringEnrichment?: boolean;
  onColumnEditorClose: () => void;
  /** When Column Editor toolbar button is clicked: open editor for current selection's first column. Returns true if a column was set. */
  onOpenColumnEditorForSelection?: () => boolean | void;
  onPanelStateChange?: (isOpen: boolean) => void;
  /** Called when the user opens the CRM Sync panel. Used to e.g. refresh sheet if a column was renamed while panel was closed. */
  onCrmSyncPanelOpen?: () => void;
  onColumnUpdated?: (updates: Partial<ColumnEditorInfo>) => void;
  onGtmEngineerComplete?: () => void;
  /** Row indices currently hidden by the sheet filter; CRM sync will exclude these rows. */
  getFilteredOutRowIds?: () => number[] | null;
  /** When true (e.g. from onboarding via navigation `state.openPrioritization`), open Prioritize Prospects immediately. */
  autoOpenPrioritization?: boolean;
  /** Called once after auto-open runs (opens panel or skips because user chose another tool); parent should clear navigation state. */
  onAutoOpenPrioritizationConsumed?: () => void;
  /** Current spreadsheet selection for scheduling cell enrichment (row_ids, column_names). */
  getEnrichmentSelection?: () => {
    row_ids: number[];
    column_names: string[];
  } | null;
  /** When true, AI GTM Engineer is expanded to the main area and the spreadsheet is not mounted. */
  aiGtmEngineerExpanded?: boolean;
  onAiGtmEngineerExpandedChange?: (expanded: boolean) => void;
  /** When GTM is full screen: Column nav exits full screen and closes the tool panel (spreadsheet visible). */
  onGtmFullScreenExitFromColumnNav?: () => void;
  /** When true, prospect prioritization is running on the spreadsheet (panel button state). */
  isProspectPrioritizationActive?: boolean;
  spreadsheetRef?: RefObject<SpreadsheetRefHandle | null>;
}

const SidebarComponent = (props: SidebarComponentProps) => {
  const theme = useTheme();
  const [selectedTool, setSelectedTool] = useState<ToolOption | null>(null);
  const [campaignState] = useCampaignContextController();
  const viewType = useSpreadsheetViewType();
  const currentCampaign = campaignState.currentCampaign;
  const isAccountCampaign =
    currentCampaign?.prospect_type === CampaignProspectType.ACCOUNT;
  const isLeadView = viewType === 'lead';

  const sidebarChromeBg = currentCampaign
    ? resolveCampaignHeaderBackground(
        theme.customColors,
        currentCampaign.prospect_type,
        viewType
      )
    : theme.customColors.themeTertiary;

  const handleToolSelect = (tool: ToolOption | null) => {
    setSelectedTool(tool);
  };

  const handleColumnButtonClick = () => {
    if (selectedTool === ToolOption.COLUMN_EDITOR) {
      props.onColumnEditorClose();
      setSelectedTool(null);
    } else if (props.spreadsheetJobLocked) {
      // Do not open column editor while spreadsheet is locked
      return;
    } else if (
      props.aiGtmEngineerExpanded &&
      props.onGtmFullScreenExitFromColumnNav
    ) {
      props.onGtmFullScreenExitFromColumnNav();
      setSelectedTool(null);
    } else if (props.onOpenColumnEditorForSelection) {
      const opened = props.onOpenColumnEditorForSelection();
      if (opened || props.columnEditorColumn)
        setSelectedTool(ToolOption.COLUMN_EDITOR);
    } else if (props.columnEditorColumn) {
      setSelectedTool(ToolOption.COLUMN_EDITOR);
    } else {
      setSelectedTool(ToolOption.COLUMN_EDITOR);
    }
  };

  // When column header is clicked from spreadsheet, open sidebar with column editor.
  // Only switch TO column editor when column is first selected—allow user to switch to other panels.
  const prevColumnEditorRef = useRef<typeof props.columnEditorColumn>(null);
  const prevSelectedToolRef = useRef<ToolOption | null>(null);

  useEffect(() => {
    if (
      selectedTool === ToolOption.CRM_SYNC &&
      prevSelectedToolRef.current !== ToolOption.CRM_SYNC
    ) {
      props.onCrmSyncPanelOpen?.();
    }
    prevSelectedToolRef.current = selectedTool;
  }, [selectedTool, props.onCrmSyncPanelOpen]);

  useEffect(() => {
    const prev = prevColumnEditorRef.current;
    prevColumnEditorRef.current = props.columnEditorColumn;

    if (props.columnEditorColumn) {
      if (!prev) {
        setSelectedTool(ToolOption.COLUMN_EDITOR);
      }
    } else if (selectedTool === ToolOption.COLUMN_EDITOR) {
      setSelectedTool(null);
    }
  }, [props.columnEditorColumn, selectedTool]);

  // When spreadsheet becomes locked by a background job, close column editor panel if open
  useEffect(() => {
    if (
      props.spreadsheetJobLocked &&
      selectedTool === ToolOption.COLUMN_EDITOR
    ) {
      props.onColumnEditorClose();
      setSelectedTool(null);
    }
  }, [
    props.spreadsheetJobLocked,
    selectedTool,
    props.onColumnEditorClose
  ]);

  const autoOpenPrioritizationDoneRef = useRef(false);
  useEffect(() => {
    if (!props.autoOpenPrioritization) {
      autoOpenPrioritizationDoneRef.current = false;
      return;
    }
    if (autoOpenPrioritizationDoneRef.current) return;
    if (selectedTool !== null) {
      autoOpenPrioritizationDoneRef.current = true;
      props.onAutoOpenPrioritizationConsumed?.();
      return;
    }
    setSelectedTool(ToolOption.PROSPECT_PRIORITIZATION);
    autoOpenPrioritizationDoneRef.current = true;
    props.onAutoOpenPrioritizationConsumed?.();
  }, [
    props.autoOpenPrioritization,
    props.onAutoOpenPrioritizationConsumed,
    selectedTool
  ]);

  // On lead view (account campaigns), Target Persona and Scheduling are hidden—close panel if open.
  useEffect(() => {
    if (
      isAccountCampaign &&
      isLeadView &&
      (selectedTool === ToolOption.TARGET_PERSONA ||
        selectedTool === ToolOption.SCHEDULING)
    ) {
      setSelectedTool(null);
    }
  }, [isAccountCampaign, isLeadView, selectedTool]);

  const onAiGtmEngineerExpandedChange = props.onAiGtmEngineerExpandedChange;
  useEffect(() => {
    if (selectedTool !== ToolOption.AI_ASSISTANT) {
      onAiGtmEngineerExpandedChange?.(false);
    }
  }, [selectedTool, onAiGtmEngineerExpandedChange]);

  // Derive from content to avoid race: panel only open when we have something to show.
  // COLUMN_EDITOR requires columnEditorColumn; other tools are standalone—opened from nav.
  const contentTools = [ToolOption.COLUMN_EDITOR];
  const isPanelOpen =
    props.columnEditorColumn !== null ||
    (selectedTool !== null && !contentTools.includes(selectedTool));

  const effectiveTool =
    selectedTool ??
    (props.columnEditorColumn ? ToolOption.COLUMN_EDITOR : null);

  const handleCloseToolPanel = () => {
    if (effectiveTool === ToolOption.COLUMN_EDITOR) {
      props.onColumnEditorClose();
    }
    setSelectedTool(null);
  };

  useEffect(() => {
    if (props.onPanelStateChange) {
      props.onPanelStateChange(isPanelOpen);
    }
  }, [isPanelOpen, props]);

  return (
    <Box
      sx={{
        marginTop: '0px',
        paddingTop: '0px',
        paddingLeft: '0px',
        mx: 0,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        boxSizing: 'border-box',
        overflow: 'visible',
        position: 'relative',
        height: '100%',
        ...(props.aiGtmEngineerExpanded
          ? {
              flex: 1,
              minWidth: 0,
              width: '100%',
              flexShrink: 1,
              alignSelf: 'stretch'
            }
          : { flexShrink: 0 }),
        transition:
          'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s ease, border-left-color 0.2s ease',
        backgroundColor: sidebarChromeBg
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          borderRight: `1px solid ${theme.customColors.themeTertiary}`,
          height: '100%'
        }}
      >
        <WebNavSidebar
          isManager={props.isManager}
          isAdmin={props.isAdmin}
          isBeta={props.isBeta}
          selectedTab={selectedTool}
          onTabSelect={handleToolSelect}
          onColumnButtonClick={handleColumnButtonClick}
        />
      </Box>
      <ToolPanel
        isOpen={isPanelOpen}
        getEnrichmentSelection={props.getEnrichmentSelection}
        selectedTool={selectedTool}
        columnEditorColumn={props.columnEditorColumn}
        spreadsheetJobLocked={props.spreadsheetJobLocked}
        spreadsheetLockedDuringEnrichment={
          props.spreadsheetLockedDuringEnrichment
        }
        onCloseToolPanel={handleCloseToolPanel}
        onColumnUpdated={props.onColumnUpdated}
        onGtmEngineerComplete={props.onGtmEngineerComplete}
        getFilteredOutRowIds={props.getFilteredOutRowIds}
        spreadsheetRef={props.spreadsheetRef}
        isProspectPrioritizationActive={props.isProspectPrioritizationActive}
        aiGtmEngineerExpanded={props.aiGtmEngineerExpanded}
        onAiGtmEngineerExpandedChange={props.onAiGtmEngineerExpandedChange}
      />
    </Box>
  );
};

export default SidebarComponent;
